import React, { useState, useEffect } from 'react';
import {
    FileText, ArrowRight, Loader, AlertTriangle, Check, BookOpen,
    Upload, Target, List, Layers, Plus, Trash2, Edit2, Save,
    RefreshCw, X, FileInput, Copy, CheckCircle, Layout, Type,
    CheckSquare, Sparkles, Image as LucideImage, Image as ImageIcon,
    AlignLeft, Download, Loader2, Settings, Brain, Edit3
} from 'lucide-react';
import { fetchWithGeminiResilience } from './geminiClient';


// --- PROMPTS ---
const METADATA_PROMPT = `
Analise o texto da prova fornecido e extraia:
1. A Matéria/Disciplina principal (ex: Matemática, História).
2. O Ano/Série escolar provável (ex: 6º Ano, 1º Ano Ensino Médio).
3. O número de questões identificadas.

Retorne APENAS um JSON neste formato, sem markdown:
{
  "subject": "Matéria identificada",
  "grade": "Série identificada",
  "questionCount": 10
}
`.trim();

const SYSTEM_PROMPT = `
Você é um especialista em reformulação de provas escolares.
Sua tarefa é criar NOVAS VERSÕES de questões baseadas em uma prova original, mantendo o MESMO NÍVEL de dificuldade e o MESMO CONTEÚDO (Objeto de Conhecimento), mas alterando os valores, contextos ou a forma da pergunta para evitar cola.

Para cada questão original identificada no texto:
1. Identifique o "Objeto de Conhecimento" (o conceito testado, ex: "Equação de 1º Grau", "Revolução Francesa").
2. Crie uma NOVA questão inédita sobre esse mesmo objeto.
3. Se for exatas, mude os números mas mantenha a lógica de resolução.
4. Se for humanas, mude o contexto ou a fonte histórica, mas cobre o mesmo conceito.
5. Gere o Gabarito com a resposta correta.
6. Adicione uma "Justificativa Pedagógica" explicando o que está sendo avaliado.
7. Sugira um "Prompt para Imagem" se a questão se beneficiar de um suporte visual (ex: mapa, geometria).
8. IMPORTANTE: Escreva a nova questão, o gabarito e a justificativa ESTRITAMENTE NO MESMO IDIOMA em que a prova original estiver (Ex: se estiver em inglês, responda em inglês).

Retorne APENAS um JSON (sem crases de markdown) com a lista de questões geradas:
[
  {
    "originalSummary": "Resumo da questão original...",
    "object": "Objeto de Conhecimento identificado",
    "newQuestion": "Texto da nova questão reformulada...",
    "answer": "Gabarito da nova questão",
    "justification": "Explicação do que a questão avalia...",
    "imagePrompt": "Descrição detalhada para gerar uma imagem de suporte (ou null se não precisar)"
  }
]
`.trim();

// --- UTILS ---
const cleanAndParseJSON = (responseText) => {
    try {
        let clean = responseText.replace(/```json/g, '').replace(/```/g, '').trim();
        return JSON.parse(clean);
    } catch (e) {
        console.error("Erro ao parsear JSON da IA:", responseText);
        throw new Error("A IA retornou um formato inválido. Tente novamente.");
    }
};


// --- COMPONENT REFORMULADOR DE PROVA ---


export const ReformuladorProva = ({ apiKey: propApiKey }) => {
    // --- STATE ---
    // Use prop if available, otherwise check localStorage, then default
    const [apiKey, setApiKey] = useState(propApiKey || localStorage.getItem('gemini_api_key') || "AQ.Ab8RN6L9xqG2T6VdoL7ChC1S_aV8DS1a1UWdXhScIx9CHlwsHQ");

    // AUTO-FALLBACK MODELS LIST (Priority Order)
    const MODEL_PRIORITY = [
        "gemini-3.7-flash",
        "gemini-3.5-flash-lite"
    ];

    const [step, setStep] = useState(1); // 1: Input, 2: Loading, 3: Result
    const [formData, setFormData] = useState({
        subject: '',
        grade: '',
        versions: 1,
        originalExam: '', // Texto digitado ou extraído de .txt
        attachment: null  // Objeto para PDF ou Imagem: { base64, mimeType, fileName }
    });
    const [generatedExam, setGeneratedExam] = useState(null);
    const [error, setError] = useState(null);
    const [loadingText, setLoadingText] = useState('Analisando questões...');
    const [isAutoFilling, setIsAutoFilling] = useState(false);
    const [regeneratingIndices, setRegeneratingIndices] = useState({}); // { [index]: boolean }
    const [generatingImageIndices, setGeneratingImageIndices] = useState({}); // { [index]: boolean }

    // --- HELPER: API Call with Automatic Model Fallback ---
    const callGeminiWithFallback = async (bodyPayload) => {
        try {
            return await fetchWithGeminiResilience(apiKey, bodyPayload);
        } catch (e) {
            console.error("All models failed via geminiClient:", e);
            throw new Error(`Falha na IA. Detalhes: ${e.message}`);
        }
    };


    useEffect(() => {
        // If prop provides a key, update state to match it
        if (propApiKey && propApiKey !== apiKey) {
            setApiKey(propApiKey);
        }
    }, [propApiKey, apiKey]);


    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Reset manual text if file is uploaded
        setFormData(prev => ({ ...prev, originalExam: '' }));

        setError(null);
        setIsAutoFilling(true);

        try {
            // 1. Convert File to Base64
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = async () => {
                const base64Data = reader.result.split(',')[1];
                const mimeType = file.type;

                // Store attachment
                setFormData(prev => ({
                    ...prev,
                    attachment: { base64: base64Data, mimeType, fileName: file.name }
                }));

                // 2. Extract Metadata with Gemini
                try {
                    const parts = [
                        { text: METADATA_PROMPT },
                        { inlineData: { mimeType: mimeType, data: base64Data } }
                    ];

                    const response = await callGeminiWithFallback({
                        contents: [{ parts: parts }],
                        systemInstruction: { parts: [{ text: METADATA_PROMPT }] },
                        generationConfig: { responseMimeType: "application/json" }
                    });

                    const data = await response.json();
                    const metadata = cleanAndParseJSON(data.candidates[0].content.parts[0].text);

                    setFormData(prev => ({
                        ...prev,
                        subject: metadata.subject || '',
                        grade: metadata.grade || '',
                    }));
                } catch (metaError) {
                    console.warn("Falha ao extrair metadados automáticos:", metaError);
                    // Non-fatal, user can fill manually
                } finally {
                    setIsAutoFilling(false);
                }
            };
        } catch (fileError) {
            console.error(fileError);
            setError("Erro ao processar arquivo. Tente um PDF ou Imagem menor.");
            setIsAutoFilling(false);
        }
    };

    const handleGenerate = async () => {
        if (!formData.originalExam && !formData.attachment) {
            setError("Por favor, cole o texto da prova ou faça upload de um arquivo.");
            return;
        }

        setStep(2);
        setError(null);
        setLoadingText('A IA está lendo sua prova e criando novas questões...');

        try {
            // Prepare Input Parts
            let parts = [];
            if (formData.attachment) {
                parts.push({
                    inlineData: {
                        mimeType: formData.attachment.mimeType,
                        data: formData.attachment.base64
                    }
                });
                parts.push({ text: "Baseado neste arquivo de prova:" });
            } else {
                parts.push({ text: `Baseado neste texto de prova:\n${formData.originalExam}` });
            }

            // Add Command
            parts.push({ text: SYSTEM_PROMPT });

            const response = await callGeminiWithFallback({
                contents: [{ parts: parts }],
                systemInstruction: { parts: [{ text: SYSTEM_PROMPT }] },
                generationConfig: {
                    temperature: 0.7,
                    maxOutputTokens: 8192,
                    responseMimeType: "application/json"
                }
            });

            const data = await response.json();
            const result = cleanAndParseJSON(data.candidates[0].content.parts[0].text);

            setGeneratedExam(result);
            setStep(3);

        } catch (e) {
            console.error(e);
            setError(e.message || "Erro ao gerar prova. Verifique sua conexão ou tente novamente.");
            setStep(1);
        }
    };

    const regenerateSpecificQuestion = async (index, type) => {
        // Set loading state for this specific index
        setRegeneratingIndices(prev => ({ ...prev, [index]: true }));

        try {
            const questionToRefine = generatedExam[index];
            const prompt = `
            Refaça a seguinte questão sobre "${questionToRefine.object}".
            
            Questão Atual:
            """
            ${questionToRefine.newQuestion}
            """
            
            OBJETIVO: Crie uma variante do tipo "${type}".
            - Se for "Verdadeiro/Falso", crie 3 afirmações para julgar.
            - Se for "Relacionar Colunas", crie 4 itens para associar.
            - Se for "Assertivas", crie 3 ou 4 sentenças (I, II, III...) e OBRIGATORIAMENTE crie as 5 alternativas de (A) a (E) combinando as sentenças.
            - Se for "Discursiva", crie uma pergunta aberta abrangente.
            - Se for "Múltipla Escolha", crie uma pergunta com 5 alternativas de (A) a (E).
            - Se for "Mesmo Modelo", crie uma nova questão usando exatamente a mesma estrutura da original.
            
            IMPORTANTE / REGRAS:
            1. IDIOMA (MANDATORY TRANSLATION RULE): You MUST write your ENTIRE JSON response (the text of the question, the answer, and the justification) in the EXACT SAME LANGUAGE as the "Questão Atual". For example, if "Questão Atual" is written in English, you MUST generate the new question in English. Do NOT output Portuguese unless the original is Portuguese!
            2. FORMATO: Todas as sentenças (I, II, III) e TODAS as opções/alternativas (A, B, C, D, E) DEVEM ser escritas EXPLICITAMENTE DENTRO do campo de texto "newQuestion" (use \n para pular linhas). NUNCA omita as opções de resposta no JSON se for múltipla escolha ou assertivas.

            Retorne APENAS o JSON da nova questão:
            {
               "newQuestion": "Texto completo da nova questão (mesmo idioma da original), incluindo opções...",
               "answer": "Gabarito da nova questão (mesmo idioma)...",
               "justification": "Justificativa (mesmo idioma)...",
               "imagePrompt": "..."
            }
            `;

            const response = await callGeminiWithFallback({
                systemInstruction: { parts: [{ text: "Regra Absoluta: Responda EXATAMENTE no mesmo idioma da 'Questão Atual'. Se a questão atual estiver em inglês, toda a sua resposta (newQuestion, answer, justification) DEVE ser escrita em inglês. NUNCA traduza para o português a menos que a original seja em português." }] },
                contents: [{ parts: [{ text: prompt }] }],
                generationConfig: { responseMimeType: "application/json" }
            });

            const data = await response.json();
            const newQuestionData = cleanAndParseJSON(data.candidates[0].content.parts[0].text);

            // Update state
            const newExam = [...generatedExam];
            newExam[index] = { ...newExam[index], ...newQuestionData };
            setGeneratedExam(newExam);

        } catch (e) {
            alert("Erro ao regenerar questão: " + e.message);
        } finally {
            setRegeneratingIndices(prev => ({ ...prev, [index]: false }));
        }
    };

    const generateQuestionImage = async (index, prompt) => {
        setGeneratingImageIndices(prev => ({ ...prev, [index]: true }));

        const imageModels = [
            "gemini-3.1-flash-image",
            "gemini-3-pro-image",
            "gemini-3.1-flash-lite-image"
        ];

        let success = false;
        let lastErrorMsg = "";

        for (const model of imageModels) {
            try {
                console.log(`[Gemini Image] Tentando gerar imagem com o modelo: ${model}`);
                const url = `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${apiKey}`;
                const response = await fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        contents: [
                            {
                                parts: [
                                    { text: prompt }
                                ]
                            }
                        ],
                        generationConfig: {
                            responseModalities: ["IMAGE"]
                        }
                    })
                });

                if (!response.ok) {
                    const errData = await response.json().catch(() => ({}));
                    throw new Error(errData.error?.message || `Erro HTTP ${response.status}`);
                }

                const data = await response.json();
                
                // Find inlineData in the parts array
                const candidate = data.candidates?.[0];
                const parts = candidate?.content?.parts || [];
                const imagePart = parts.find(p => p.inlineData);
                
                if (!imagePart || !imagePart.inlineData?.data) {
                    throw new Error("A API não retornou dados de imagem em formato inlineData.");
                }

                const mimeType = imagePart.inlineData.mimeType || "image/png";
                const base64 = imagePart.inlineData.data;
                const imageSrc = `data:${mimeType};base64,${base64}`;

                // Update state
                const newExam = [...generatedExam];
                newExam[index] = { ...newExam[index], generatedImage: imageSrc };
                setGeneratedExam(newExam);
                success = true;
                break; // Exit loop on success
            } catch (e) {
                console.warn(`[Gemini Image] Falha no modelo ${model}:`, e.message);
                lastErrorMsg = e.message;
            }
        }

        if (!success) {
            console.warn(`[Gemini Image] Usando fallback gratuito do Pollinations devido ao erro: ${lastErrorMsg}`);
            const fallbackUrl = `https://image.pollinations.ai/prompt/${encodeURIComponent(prompt)}?width=512&height=512&nologo=true`;
            
            const newExam = [...generatedExam];
            newExam[index] = { ...newExam[index], generatedImage: fallbackUrl };
            setGeneratedExam(newExam);
        }

        setGeneratingImageIndices(prev => ({ ...prev, [index]: false }));
    };

    const handleQuestionEdit = (index, field, value) => {
        const newExam = [...generatedExam];
        newExam[index][field] = value;
        setGeneratedExam(newExam);
    };

    const deleteQuestion = (index) => {
        if (window.confirm('Remover esta questão?')) {
            const newExam = [...generatedExam];
            newExam.splice(index, 1);
            setGeneratedExam(newExam);
        }
    };

    const addNewQuestion = () => {
        setGeneratedExam([...generatedExam, {
            originalSummary: 'Nova Questão Manual',
            object: 'Tópico Livre',
            newQuestion: 'Digite o enunciado da questão aqui...',
            answer: 'Gabarito',
            justification: 'Criada manualmente pelo professor.'
        }]);
    };

    // --- COPY FUNCTIONALITY ---
    const handleCopy = () => {
        if (!generatedExam) return;

        // Format the content for clipboard
        const textContent = generatedExam.map((q, i) => {
            return `Questão ${i + 1}\n\n${q.newQuestion}\n\nGabarito: ${q.answer}\n`;
        }).join('\n-------------------\n\n');

        navigator.clipboard.writeText(textContent).then(() => {
            // Optional: Show a toast or temporary success state
            const btn = document.getElementById('btn-copy-exam');
            if (btn) {
                const originalText = btn.innerText;
                btn.innerText = 'Copiado!';
                setTimeout(() => btn.innerText = originalText, 2000);
            }
        }).catch(err => {
            console.error('Failed to copy: ', err);
            alert('Erro ao copiar para a área de transferência.');
        });
    };


    // --- RENDERS ---

    const renderStep1 = () => (
        <div className="max-w-4xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="text-center mb-10">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-orange-100 text-orange-600 rounded-3xl mb-6 shadow-lg rotate-3 hover:rotate-6 transition-transform">
                    <RefreshCw size={40} />
                </div>
                <h1 className="text-4xl font-extrabold text-slate-800 mb-2 tracking-tight">
                    Reformulador de Provas
                </h1>
                <p className="text-lg text-slate-500 max-w-xl mx-auto">
                    Transforme provas antigas em novas avaliações inéditas.
                    A IA mantém o nível e os conteúdos, mas cria questões totalmente novas.
                </p>
                {error && (
                    <div className="mt-6 p-4 bg-red-50 text-red-600 rounded-xl border border-red-100 flex items-center justify-center gap-2 animate-pulse">
                        <AlertTriangle size={20} />
                        <span className="font-medium">{error}</span>
                    </div>
                )}
            </div>

            <div className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden">
                <div className="p-1 border-b border-slate-100 flex gap-1 bg-slate-50/50">
                    <button className="flex-1 py-3 text-sm font-semibold text-orange-600 bg-white rounded-xl shadow-sm border border-slate-200">
                        <FileText size={16} className="inline mr-2" />
                        Upload ou Texto
                    </button>
                    {/* Add more tabs if needed */}
                </div>

                <div className="p-8 grid md:grid-cols-2 gap-10">
                    {/* Left: Metadata */}
                    <div className="space-y-6">
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2">Matéria / Disciplina</label>
                            <input
                                type="text"
                                className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-orange-200 focus:border-orange-400 outline-none transition-all font-medium"
                                placeholder="Ex: História, Logaritmos..."
                                value={formData.subject}
                                onChange={e => setFormData({ ...formData, subject: e.target.value })}
                                disabled={isAutoFilling}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2">Série / Ano</label>
                            <input
                                type="text"
                                className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-orange-200 focus:border-orange-400 outline-none transition-all font-medium"
                                placeholder="Ex: 8º Ano, 3º Médio..."
                                value={formData.grade}
                                onChange={e => setFormData({ ...formData, grade: e.target.value })}
                                disabled={isAutoFilling}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2">Versões Diferentes</label>
                            <select
                                className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-orange-200 focus:border-orange-400 outline-none transition-all font-medium"
                                value={formData.versions}
                                onChange={e => setFormData({ ...formData, versions: Number(e.target.value) })}
                            >
                                <option value={1}>1 Versão (Padrão)</option>
                                <option value={2}>2 Versões (Tipo A e B)</option>
                                <option value={3}>3 Versões</option>
                            </select>
                        </div>
                    </div>

                    {/* Right: Input */}
                    <div className="space-y-6">
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2">
                                <span className="flex items-center gap-2">
                                    Conteúdo da Prova Original
                                    {isAutoFilling && <Loader className="w-3 h-3 animate-spin text-orange-500" />}
                                </span>
                            </label>

                            <div className="relative group">
                                <textarea
                                    className="w-full h-48 p-4 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-orange-200 focus:border-orange-400 outline-none transition-all resize-none text-sm leading-relaxed"
                                    placeholder="Cole o texto da prova aqui..."
                                    value={formData.originalExam}
                                    onChange={e => setFormData({ ...formData, originalExam: e.target.value })}
                                    disabled={!!formData.attachment}
                                />
                                {formData.attachment && (
                                    <div className="absolute inset-0 bg-slate-100/90 flex flex-col items-center justify-center rounded-xl backdrop-blur-sm">
                                        <FileText size={40} className="text-slate-400 mb-2" />
                                        <span className="text-sm font-semibold text-slate-700">{formData.attachment.fileName}</span>
                                        <button
                                            onClick={() => setFormData({ ...formData, attachment: null })}
                                            className="mt-2 text-xs text-red-500 hover:underline"
                                        >
                                            Remover arquivo
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="relative">
                            <input
                                type="file"
                                id="exam-upload"
                                className="hidden"
                                accept=".pdf, .txt, image/*"
                                onChange={handleFileChange}
                            />
                            <label
                                htmlFor="exam-upload"
                                className="flex items-center justify-center w-full p-4 border-2 border-dashed border-slate-200 rounded-xl text-slate-500 hover:border-orange-400 hover:text-orange-600 hover:bg-orange-50 transition-all cursor-pointer gap-3 group"
                            >
                                <div className="p-2 bg-slate-100 rounded-full group-hover:bg-orange-100 transition-colors">
                                    <Upload size={20} />
                                </div>
                                <span className="font-medium text-sm">Carregar PDF ou Imagem</span>
                            </label>
                        </div>
                    </div>
                </div>

                <div className="p-6 bg-slate-50 border-t border-slate-100 flex justify-end">
                    <button
                        onClick={handleGenerate}
                        disabled={!formData.originalExam && !formData.attachment}
                        className="flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-orange-600 to-orange-500 text-white rounded-xl shadow-lg hover:shadow-orange-500/30 hover:translate-y-[-2px] active:translate-y-[0px] disabled:opacity-50 disabled:hover:translate-y-0 transition-all font-bold text-lg"
                    >
                        Gerar Nova Prova
                        <ArrowRight size={20} />
                    </button>
                </div>
            </div>
        </div>
    );

    const renderStep2 = () => (
        <div className="max-w-md mx-auto min-h-[60vh] flex flex-col items-center justify-center text-center animate-in fade-in zoom-in-95 duration-500">
            <div className="relative">
                <div className="w-24 h-24 border-4 border-slate-100 border-t-orange-500 rounded-full animate-spin"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                    <Sparkles className="text-orange-500 animate-pulse" size={32} />
                </div>
            </div>
            <h2 className="mt-8 text-2xl font-bold text-slate-800">Reformulando Prova</h2>
            <p className="mt-3 text-slate-500 text-lg max-w-xs mx-auto leading-relaxed">
                {loadingText}
            </p>
        </div>
    );

    const renderStep3 = () => (
        <div className="max-w-5xl mx-auto animate-in fade-in slide-in-from-bottom-8 duration-700">
            <div className="flex items-center justify-between mb-8 print:hidden">
                <div>
                    <button
                        onClick={() => setStep(1)}
                        className="flex items-center text-slate-500 hover:text-slate-800 transition-colors mb-2"
                    >
                        <ArrowRight className="rotate-180 mr-2" size={16} /> Voltar
                    </button>
                    <h1 className="text-3xl font-extrabold text-slate-900">Prova Reformulada</h1>
                    <p className="text-slate-500">{formData.subject} • {formData.grade}</p>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={addNewQuestion}
                        className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 font-medium shadow-sm"
                    >
                        <Plus size={18} /> Adicionar
                    </button>

                    <button
                        id="btn-copy-exam"
                        onClick={handleCopy}
                        className="flex items-center gap-2 px-4 py-2 bg-indigo-50 border border-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-100 font-medium shadow-sm transition-colors"
                    >
                        <Copy size={18} /> Copiar Texto
                    </button>

                    <button
                        onClick={() => window.print()}
                        className="flex items-center gap-2 px-6 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 font-bold shadow-lg shadow-slate-900/20"
                    >
                        <Save size={18} /> Salvar / Imprimir
                    </button>
                </div>
            </div>

            <div className="bg-white shadow-xl shadow-slate-200/50 rounded-none md:rounded-xl overflow-hidden min-h-[29.7cm] print:shadow-none print:w-full">

                {/* Header da Prova (Visualização Impressa) */}
                <div className="bg-white border-b-2 border-black p-8 hidden print:block">
                    <div className="flex justify-between items-start mb-6">
                        <img src="https://logodownload.org/wp-content/uploads/2017/08/colegio-eleva-logo.png" className="h-12 grayscale" alt="Logo" />
                        <div className="text-right">
                            <h2 className="text-xl font-bold uppercase tracking-widest">{formData.subject}</h2>
                            <p className="text-sm text-gray-600">{formData.grade} • Avaliação Mensal</p>
                        </div>
                    </div>
                    <div className="border border-black p-4 flex gap-8 text-sm">
                        <div className="flex-1">Nome: __________________________________________________________________</div>
                        <div className="w-32">Data: ___/___/___</div>
                    </div>
                </div>

                {/* Lista de Questões */}
                {Array.isArray(generatedExam) && generatedExam.map((q, index) => (
                    <div key={index} className="group border-b border-slate-100 hover:bg-slate-50 transition-colors print:break-inside-avoid">

                        {/* Editor Toolbar */}
                        <div className="flex items-center justify-between px-6 py-2 bg-slate-50 border-b border-slate-100 opacity-0 group-hover:opacity-100 transition-opacity print:hidden">
                            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Questão {index + 1}</span>
                            <div className="flex gap-1">
                                <button
                                    onClick={() => deleteQuestion(index)}
                                    className="p-1.5 hover:bg-red-100 rounded text-red-500 transition-colors"
                                    title="Remover"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                                <div className="w-px h-4 bg-slate-300 mx-2 self-center"></div>
                                <button
                                    disabled={regeneratingIndices[index]}
                                    onClick={() => regenerateSpecificQuestion(index, 'Verdadeiro/Falso')}
                                    className="p-1.5 hover:bg-slate-200 rounded text-slate-600 disabled:opacity-50"
                                    title="Verdadeiro ou Falso"
                                >
                                    <CheckSquare className="w-4 h-4" />
                                </button>
                                <button
                                    disabled={regeneratingIndices[index]}
                                    onClick={() => regenerateSpecificQuestion(index, 'Relacionar Colunas')}
                                    className="p-1.5 hover:bg-slate-200 rounded text-slate-600 disabled:opacity-50"
                                    title="Relacionar Colunas"
                                >
                                    <Layout className="w-4 h-4" />
                                </button>
                                <button
                                    disabled={regeneratingIndices[index]}
                                    onClick={() => regenerateSpecificQuestion(index, 'Assertivas')}
                                    className="p-1.5 hover:bg-slate-200 rounded text-slate-600 disabled:opacity-50"
                                    title="Assertivas (I, II e III)"
                                >
                                    <Type className="w-4 h-4" />
                                </button>
                                <div className="w-px h-4 bg-slate-300 mx-2 self-center"></div>
                                <button
                                    disabled={regeneratingIndices[index]}
                                    onClick={() => regenerateSpecificQuestion(index, 'Discursiva')}
                                    className="p-1.5 hover:bg-slate-200 rounded text-slate-600 disabled:opacity-50"
                                    title="Discursiva (Aberta)"
                                >
                                    <AlignLeft className="w-4 h-4" />
                                </button>
                                <button
                                    disabled={regeneratingIndices[index]}
                                    onClick={() => regenerateSpecificQuestion(index, 'Múltipla Escolha')}
                                    className="p-1.5 hover:bg-slate-200 rounded text-slate-600 disabled:opacity-50"
                                    title="Múltipla Escolha (A a E)"
                                >
                                    <List className="w-4 h-4" />
                                </button>
                                <button
                                    disabled={regeneratingIndices[index]}
                                    onClick={() => regenerateSpecificQuestion(index, 'Mesmo Modelo')}
                                    className="p-1.5 hover:bg-slate-200 rounded text-slate-600 disabled:opacity-50"
                                    title="Refazer no Mesmo Modelo"
                                >
                                    <Copy className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        <div className="p-6 relative">
                            {regeneratingIndices[index] && (
                                <div className="absolute inset-0 bg-white/80 z-10 flex items-center justify-center backdrop-blur-sm transition-all">
                                    <div className="flex flex-col items-center gap-2 animate-bounce">
                                        <RefreshCw className="w-8 h-8 text-slate-900 animate-spin" />
                                        <span className="text-sm font-semibold text-slate-700">Reescrevendo questão...</span>
                                    </div>
                                </div>
                            )}

                            <div className="flex gap-4">
                                <span className="font-bold text-xl text-black print:text-black">{index + 1}.</span>
                                <div className="flex-1 w-full">
                                    {/* The Question Text */}
                                    <div className="relative mb-4">
                                        <textarea
                                            value={q.newQuestion || ''}
                                            onChange={(e) => handleQuestionEdit(index, 'newQuestion', e.target.value)}
                                            className="w-full min-h-[100px] resize-y bg-transparent border-none p-0 focus:ring-0 text-slate-900 print:text-black font-sans text-base leading-relaxed overflow-hidden"
                                            rows={Math.max(3, (q.newQuestion || '').split('\n').length)}
                                        />
                                    </div>

                                    {/* Images */}
                                    {q.generatedImage && (
                                        <div className="my-4 relative inline-block group">
                                            <img src={q.generatedImage} alt="Imagem da questão" className="max-h-60 rounded border border-slate-200" />
                                            <button
                                                onClick={() => {
                                                    const newExam = [...generatedExam];
                                                    newExam[index].generatedImage = null;
                                                    setGeneratedExam(newExam);
                                                }}
                                                className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white p-1 rounded-full shadow transition-all opacity-0 group-hover:opacity-100 print:hidden"
                                                title="Remover Imagem"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </div>
                                    )}

                                    {/* Image Generation Prompt (optional, for manual trigger if imagePrompt exists but no image yet) */}
                                    {!q.generatedImage && q.imagePrompt && (
                                        <div className="print:hidden mt-2 p-2 bg-blue-50 rounded border border-blue-100 flex items-center justify-between">
                                            <span className="text-xs text-blue-700 flex items-center gap-1">
                                                <LucideImage className="w-3 h-3" /> Sugerida imagem: "{q.imagePrompt.substring(0, 50)}..."
                                            </span>
                                            <button
                                                onClick={() => generateQuestionImage(index, q.imagePrompt)}
                                                padding="px-2 py-1"
                                                className="text-xs bg-blue-600 text-white rounded px-2 py-1 flex items-center gap-1"
                                                disabled={generatingImageIndices[index]}
                                            >
                                                {generatingImageIndices[index] ? <Loader className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                                                Gerar Imagem
                                            </button>
                                        </div>
                                    )}

                                    {q.justification && (
                                        <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-800 print:hidden">
                                            <strong>Nota Pedagógica:</strong> {q.justification}
                                        </div>
                                    )}
                                    {q.answer && (
                                        <div className="mt-2 text-sm text-slate-500 print:hidden">
                                            <strong>Gabarito:</strong> {q.answer}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Footer Statistics for the Question */}
                        <div className="bg-slate-50 px-6 py-2 border-t border-slate-100 flex justify-between items-center text-[10px] text-slate-400 uppercase tracking-wider print:hidden">
                            <span>Ref: {q.originalSummary ? q.originalSummary.substring(0, 30) : 'Questão Original'}...</span>
                            <span>Objeto: {q.object}</span>
                        </div>

                    </div>
                ))}
            </div>

            {/* Gabarito Section for Print */}
            <div className="hidden print:block mt-20 break-before-page">
                <h2 className="text-xl font-bold mb-4">Gabarito Oficial</h2>
                <table className="w-full border-collapse border border-black">
                    <thead>
                        <tr>
                            <th className="border border-black p-2">Questão</th>
                            <th className="border border-black p-2">Resposta</th>
                        </tr>
                    </thead>
                    <tbody>
                        {Array.isArray(generatedExam) && generatedExam.map((q, i) => (
                            <tr key={i}>
                                <td className="border border-black p-2 text-center w-20">{i + 1}</td>
                                <td className="border border-black p-2">{q.answer}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

        </div>
    );

    return (
        <div className="min-h-screen bg-slate-50 p-6 md:p-12 font-sans relative">
            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-orange-900 to-orange-300 z-10"></div>

            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
        </div>
    );
}
export default ReformuladorProva;
